import React, { useState, useMemo } from "react"

import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchCard from "~/library/CatchCard"
import CatchTable from "~/library/CatchTable"
import Pagination from "~/library/Pagination"
import NavigationLink from "~/library/NavigationLink"

import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import { apiWorkspaceContacts, apiWorkspaceContactCategories } from "~/routes/"
import type Contact from "~/types/Contact"

const paramifyQ = (q?: Record<string, string | number | null | undefined>) =>
    Object.fromEntries(
        Object.entries(q || {})
            .filter(([, v]) => v != null && v !== "")
            .map(([k, v]) => [`q[${k}]`, String(v)])
    )

const buildRansackQuery = (filters: { search: string; categoryId: string }) => {
    const q: Record<string, string> = {}

    if (filters.search) {
        q["first_name_or_last_name_or_email_cont"] = filters.search
    }

    if (filters.categoryId) {
        q["contact_category_id_eq"] = filters.categoryId
    }

    return q
}

const ContactsList = () => {
    const { venueSlug } = getAppConfig()

    const [filters, setFilters] = useState({
        search: "",
        categoryId: "",
    })

    const {
        data: contacts,
        refetch,
        metadata,
        isLoading,
    } = useFetchResource<{ contacts: Contact[] }, "contacts">(
        apiWorkspaceContacts.index.path({ venue_slug: venueSlug }),
        "contacts",
        { page: "1", ...paramifyQ(buildRansackQuery(filters)) }
    )

    const { data: contactCategories } = useFetchResource<
    Array<{ id: string; name: string }>,
    "contact_categories"
    >(
        apiWorkspaceContactCategories.index.path({ venue_slug: venueSlug }),
            "contact_categories",
            {}
    )

    const handleFilterChange = (field: string, value: string) => {
        const updated = { ...filters, [field]: value }
        setFilters(updated)
        refetch({ page: "1", ...paramifyQ(buildRansackQuery(updated)) })
    }

    const handlePageChange = (page: string) => {
        refetch({ page, ...paramifyQ(buildRansackQuery(filters)) })
    }

    const columns = useMemo(
        () => [
            {
                key: "firstName",
                header: "Name",
                formatter: (_: string, row: Contact) => (
                    <NavigationLink type="contacts" to={`/contacts/${row.prefixId}`}>
                        {row.firstName} {row.lastName}
                    </NavigationLink>
                ),
            },
            {
                key: "email",
                header: "Email",
                formatter: (value: string) => value || "—",
            },
            {
                key: "phone",
                header: "Phone",
                formatter: (value: string) => value || "—",
            },
            {
                key: "role",
                header: "Role",
                formatter: (value: string) => value || "—",
            },
            {
                key: "contactCategory",
                header: "Category",
                formatter: (_: any, row: Contact) => row.contactCategory?.name || "—",
            },
            {
                key: "account",
                header: "Account",
                formatter: (_: any, row: Contact) =>
                    row.account ? (
                        <NavigationLink type="accounts" to={`/accounts/${row.account.prefixId}`}>
                            {row.account.name}
                        </NavigationLink>
                    ) : (
                        "—"
                    ),
            },
        ],
        []
    )

    return (
        <LoadingWrapper isLoading={isLoading}>
            <PageTitle title="Contacts" newItemPath="/contacts/new" />

            <div className="row">
                <div className="col-12">
                    <div className="d-flex align-items-center mb-3 gap-3 flex-wrap">
                        <div className="ms-auto d-flex gap-2 flex-wrap align-items-center">
                            <select
                                className="form-select w-auto"
                                value={filters.categoryId}
                                onChange={(e) => handleFilterChange("categoryId", e.target.value)}
                            >
                                <option value="">All Categories</option>
                                {contactCategories?.map((category) => (
                                    <option key={category.id} value={category.id}>
                                        {category.name}
                                    </option>
                                ))}
                            </select>

                            <input
                                type="text"
                                className="form-control w-auto"
                                placeholder="Search contacts"
                                value={filters.search}
                                onChange={(e) => handleFilterChange("search", e.target.value)}
                            />
                        </div>
                    </div>

                    <CatchCard className="card-dark-theme">
                        <CatchTable
                            key={`${filters.search}-${filters.categoryId}`}
                            data={contacts}
                            emptyMessage="No Contacts"
                            columns={columns}
                        />
                        {metadata && (
                            <Pagination
                                metadata={metadata}
                                onPageChange={(page: number) =>
                                    handlePageChange(page.toString())
                                }
                            />
                        )}
                    </CatchCard>
                </div>
            </div>
        </LoadingWrapper>
    )
}

export default ContactsList
